"use client";

import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import {
  type CSSProperties,
  type FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { SITE } from "@/constants/site";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import type { BackgroundMusic } from "@/lib/content/music.server";
import { cn } from "@/lib/utils";

// =============================================================================
// Background lofi music. Browsers block autoplay with sound, so it starts on
// the first interaction with the page (unless the visitor paused it before),
// fades in and out, pauses while the tab is hidden and remembers the volume.
// The volume goes through a Web Audio gain: iOS ignores audio.volume.
// =============================================================================

const STORAGE_KEY = "music-preferences";
const FIRST_INTERACTIONS = ["pointerdown", "keydown", "touchend"] as const;
const EASE = [0.16, 1, 0.3, 1] as const;

type Preferences = { muted: boolean; volume: number };

function readPreferences(): Partial<Preferences> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function savePreferences(preferences: Preferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Storage disabled: the choice only lasts for this page
  }
}

/** Perceived loudness follows the square of the slider */
const toGain = (volume: number) => volume ** 2;

type AudioGraph = {
  audio: HTMLAudioElement;
  context: AudioContext;
  gain: GainNode;
};

// =============================================================================

function SpeakerIcon({ isPlaying }: { isPlaying: boolean }) {
  const shouldReduceMotion = usePrefersReducedMotion();
  const pulse = isPlaying && !shouldReduceMotion;

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-[1.15rem]"
      aria-hidden
    >
      <path d="M11 5 6.5 9H4a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h2.5l4.5 4z" />
      {[
        { d: "M15.2 9.2a4 4 0 0 1 0 5.6", delay: 0 },
        { d: "M18 6.5a8 8 0 0 1 0 11", delay: 0.18 },
      ].map(({ d, delay }) => (
        <motion.path
          key={d}
          d={d}
          initial={false}
          animate={
            isPlaying
              ? {
                  pathLength: 1,
                  opacity: pulse ? [1, 0.35, 1] : 1,
                }
              : { pathLength: 0, opacity: 0 }
          }
          transition={{
            pathLength: { duration: 0.45, delay, ease: EASE },
            opacity: pulse
              ? { duration: 1.8, delay, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.3 },
          }}
        />
      ))}
      <motion.path
        d="M16 9.5l5 5M21 9.5l-5 5"
        initial={false}
        animate={{
          pathLength: isPlaying ? 0 : 1,
          opacity: isPlaying ? 0 : 1,
        }}
        transition={{ duration: 0.4, ease: EASE }}
      />
    </svg>
  );
}

// =============================================================================

/**
 * Discreet music button of the bottom right corner: click to play or pause,
 * hover (or focus) to reveal the volume slider.
 */
export function MusicPlayer({ music }: { music: BackgroundMusic }) {
  const t = useTranslations("Music");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [volume, setVolume] = useState(music.volume);
  const [title, setTitle] = useState<string | null>(null);

  const graph = useRef<AudioGraph | null>(null);
  const trackIndex = useRef(-1);
  const pauseTimer = useRef<number | undefined>(undefined);
  const state = useRef({
    isPlaying: false,
    muted: false,
    volume: music.volume,
  });

  const remember = useCallback((changes: Partial<Preferences>) => {
    Object.assign(state.current, changes);
    savePreferences({
      muted: state.current.muted,
      volume: state.current.volume,
    });
  }, []);

  const fadeTo = useCallback((target: number, seconds: number) => {
    if (!graph.current) return;
    const { gain, context } = graph.current;
    const now = context.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(target, now + seconds);
  }, []);

  const loadTrack = useCallback(
    (index: number) => {
      const current = graph.current;
      const track = music.tracks[index];
      if (!current || !track) return;
      trackIndex.current = index;
      current.audio.src = track.url;
      current.audio.loop = music.tracks.length === 1;
      setTitle(track.title);
      if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: track.title,
          artist: track.artist ?? SITE.name,
          album: SITE.name,
        });
      }
    },
    [music.tracks]
  );

  const nextIndex = useCallback(() => {
    const count = music.tracks.length;
    if (count <= 1) return 0;
    if (!music.shuffle) return (trackIndex.current + 1) % count;
    let next = trackIndex.current;
    while (next === trackIndex.current) {
      next = Math.floor(Math.random() * count);
    }
    return next;
  }, [music.shuffle, music.tracks.length]);

  /** Audio element and Web Audio graph, created on the first play */
  const ensureGraph = useCallback((): AudioGraph | null => {
    if (graph.current) return graph.current;
    const AudioContextClass =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextClass) return null;

    const audio = new Audio();
    audio.crossOrigin = "anonymous";
    audio.preload = "auto";
    const context = new AudioContextClass();
    const gain = context.createGain();
    gain.gain.value = 0;
    context
      .createMediaElementSource(audio)
      .connect(gain)
      .connect(context.destination);
    graph.current = { audio, context, gain };

    // Several tracks: chain them, a new one each time
    audio.addEventListener("ended", () => {
      loadTrack(nextIndex());
      void audio.play();
    });
    return graph.current;
  }, [loadTrack, nextIndex]);

  const play = useCallback(async () => {
    const current = ensureGraph();
    if (!current) return;
    window.clearTimeout(pauseTimer.current);
    if (trackIndex.current < 0) {
      // First play: shuffle picks a random track, the first one otherwise
      loadTrack(music.shuffle ? nextIndex() : 0);
    }
    try {
      await current.context.resume();
      await current.audio.play();
    } catch {
      return;
    }
    fadeTo(toGain(state.current.volume), 1.8);
    state.current.isPlaying = true;
    setIsPlaying(true);
  }, [ensureGraph, fadeTo, loadTrack, music.shuffle, nextIndex]);

  const pause = useCallback(
    (fadeSeconds = 0.7) => {
      fadeTo(0, fadeSeconds);
      window.clearTimeout(pauseTimer.current);
      pauseTimer.current = window.setTimeout(
        () => graph.current?.audio.pause(),
        fadeSeconds * 1000 + 50
      );
      state.current.isPlaying = false;
      setIsPlaying(false);
    },
    [fadeTo]
  );

  const toggle = () => {
    if (state.current.isPlaying) {
      pause();
      remember({ muted: true });
    } else {
      remember({ muted: false });
      void play();
    }
  };

  const changeVolume = (value: number) => {
    setVolume(value);
    remember({ volume: value });
    if (state.current.isPlaying) fadeTo(toGain(value), 0.08);
  };

  // Saved preferences, then start on the first interaction with the page
  useEffect(() => {
    const saved = readPreferences();
    if (typeof saved.volume === "number") {
      const restored = Math.min(1, Math.max(0, saved.volume));
      state.current.volume = restored;
      setVolume(restored);
    }
    state.current.muted = saved.muted === true;

    const start = (event: Event) => {
      // The music button handles its own clicks
      if ((event.target as Element | null)?.closest?.("[data-music-player]")) {
        return;
      }
      stop();
      if (!state.current.muted) void play();
    };
    const stop = () =>
      FIRST_INTERACTIONS.forEach((name) =>
        window.removeEventListener(name, start)
      );
    FIRST_INTERACTIONS.forEach((name) =>
      window.addEventListener(name, start, { passive: true })
    );
    return stop;
  }, [play]);

  // Quiet while the tab is hidden, back when the visitor returns
  useEffect(() => {
    let resume = false;
    const onVisibilityChange = () => {
      if (document.hidden && state.current.isPlaying) {
        resume = true;
        pause(0.25);
      } else if (!document.hidden && resume) {
        resume = false;
        void play();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [pause, play]);

  // Media keys and the OS media controls
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.setActionHandler("play", () => {
      remember({ muted: false });
      void play();
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      remember({ muted: true });
      pause();
    });
    return () => {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
    };
  }, [pause, play, remember]);

  // Release the audio when the layout unmounts (locale switch)
  useEffect(
    () => () => {
      graph.current?.audio.pause();
      void graph.current?.context.close();
    },
    []
  );

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
  };

  return (
    <div
      data-music-player
      className="fixed right-[max(1rem,env(safe-area-inset-right))] bottom-[max(1rem,env(safe-area-inset-bottom))] z-40 print:hidden"
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") setIsOpen(true);
      }}
      onPointerLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={onBlur}
    >
      <div className="flex items-center rounded-full border border-foreground/10 bg-background/70 p-1 shadow-[0_10px_30px_-12px_rgb(0_0_0/0.45)] backdrop-blur-md transition-colors duration-500 hover:border-foreground/20">
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="volume"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "auto", opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{
                width: { type: "spring", stiffness: 380, damping: 34 },
                opacity: { duration: 0.25 },
              }}
              className="overflow-hidden"
            >
              <div className="flex items-center gap-3 pr-2 pl-3.5">
                {title && (
                  <span
                    className="max-w-[9rem] truncate text-xs text-muted-foreground"
                    aria-live="polite"
                  >
                    <span className="sr-only">
                      {t("nowPlaying", { title })}
                    </span>
                    <span aria-hidden>{title}</span>
                  </span>
                )}
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(volume * 100)}
                  onChange={(event) =>
                    changeVolume(Number(event.target.value) / 100)
                  }
                  aria-label={t("volume")}
                  className="range-input w-24"
                  style={{ "--value": `${volume * 100}%` } as CSSProperties}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <button
          type="button"
          onClick={toggle}
          aria-label={isPlaying ? t("pause") : t("play")}
          aria-pressed={isPlaying}
          className={cn(
            "grid size-10 shrink-0 place-items-center rounded-full transition-[background-color,color,transform] duration-300 active:scale-90",
            isPlaying
              ? "text-foreground hover:bg-foreground/10"
              : "text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
          )}
        >
          <SpeakerIcon isPlaying={isPlaying} />
        </button>
      </div>
    </div>
  );
}

// =============================================================================
